import {Component, EventEmitter, Input, Output, OnDestroy, ChangeDetectorRef} from '@angular/core';
import { DataSharingService } from '../../../services/data-sharing.service';
import { CompanyService } from '../../../services/company.service';
import { pluralMappings } from '../../../classes/i18n';
import { MessageService } from '../../../services/message.service';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import {QuotaManagementService} from '../../../services/quota-management.service';
declare var $: any;

@Component({
  selector: 'magic-bulk-unlock',
  templateUrl: './magic-bulk-unlock.component.html',
  styleUrl: './magic-bulk-unlock.component.css',
  standalone: false,
})
export class MagicBulkUnlockComponent implements OnDestroy {
  @Input() modulesInfo: Record<string, any>[];
  @Input() numberOfCompaniesMatchingSearchParams: number = null;
  companyIds: number[] = [];
  searchParams: HttpParams = null;
  selectedMagicModuleIds: number[] = [];
  selectedModuleId: number = null;
  totalCreditsCost: number = null;
  pluralMappings = pluralMappings;
  minimumCompaniesToUnlock = 1;
  maximumCompaniesToBulkUnlockViaQuery = 50000;
  selectedMagicInsightsUnlockCount = null;
  private magicUnlockCountSub: Subscription;

  monthlyQuotaDetails: any = null;
  bulkQuotaDetails: any = null;
  loadingQuotas = false;
  hasFetchedQuotas = false;

  @Output() onUnlock = new EventEmitter();

  constructor(
    private dataStore: DataSharingService,
    private companyService: CompanyService,
    private messageService: MessageService,
    private quotaManagementService: QuotaManagementService,
    private cd: ChangeDetectorRef
  ) {}


  loadingState = this.companyService.isLoading;

  ngOnInit() {
    this.selectedModuleId = this.modulesInfo.length === 1 ? this.modulesInfo[0].id : null;
    this.dataStore.currentSelectedCompanyIds.subscribe(data => {
      this.companyIds = data;
    });
    this.dataStore.currentSelectedSearchParams.subscribe(
      searchParams => (this.searchParams = searchParams)
    );
    this.magicUnlockCountSub = this.dataStore.currentMagicInsightsUnlockCount.subscribe(
      count => {
        if (count !== undefined) {
          this.selectedMagicInsightsUnlockCount = count;
          this.handleCreditCostPreview();
        }
      }
    );
    this.handleSelectedModuleIdsChange();

    $('#magicInsightsBulkUnlock').on('show.bs.modal', () => {
        if (!this.hasFetchedQuotas){
          this.fetchMagicInsightsQuotas();
        }
    })

  }

  ngOnDestroy(): void {
    this.magicUnlockCountSub?.unsubscribe();
  }


  fetchMagicInsightsQuotas() {
    // Only run if we haven't fetched yet
    if (this.hasFetchedQuotas) return;
    this.loadingQuotas = true;
    const featureName = 'Magic Insights Unlock';

    // forkJoin runs both requests in parallel and waits for both to complete
    forkJoin({
      monthly: this.quotaManagementService.getUserQuotaDetails(featureName, 'MONTHLY'),
      bulk: this.quotaManagementService.getUserQuotaDetails(featureName, 'BULK')
    }).subscribe({
      next: (results) => {
        this.monthlyQuotaDetails = results.monthly;
        this.bulkQuotaDetails = results.bulk;
        this.hasFetchedQuotas = true;
        this.loadingQuotas = false;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.loadingQuotas = false;
        this.cd.detectChanges();
      }
    });
  }


  handleSelectedModuleIdsChange() {
    this.selectedMagicModuleIds = this.selectedModuleId ? [this.selectedModuleId] : null;
    this.handleCreditCostPreview();
  }
  

  handleCreditCostPreview(): void {
    // First, check if a number has been entered. If it's null or empty, do nothing.
    if (this.selectedMagicInsightsUnlockCount !== null && this.selectedMagicInsightsUnlockCount !== undefined) {
      // If the number is greater than the maximum, clamp it to the maximum.
      if (this.selectedMagicInsightsUnlockCount > this.maximumCompaniesToBulkUnlockViaQuery) {
        this.selectedMagicInsightsUnlockCount = this.maximumCompaniesToBulkUnlockViaQuery;
      }

      // If the number is less than the minimum (but not empty), clamp it to the minimum.
      if (this.selectedMagicInsightsUnlockCount < this.minimumCompaniesToUnlock) {
        this.selectedMagicInsightsUnlockCount = this.minimumCompaniesToUnlock;
      }
    }

    if (this.companyIds?.length){
      this.totalCreditsCost = this.companyIds.length;
    }
    else if (this.searchParams){
      this.totalCreditsCost = this.selectedMagicInsightsUnlockCount || this.numberOfCompaniesMatchingSearchParams;
    }
  }


  confirmUnlockMIForSelectedCompanies(): void {
    this.companyService
      .unlockMagicInsightsModules(
        this.selectedMagicModuleIds[0],
        this.companyIds,
        this.searchParams,
        this.selectedMagicInsightsUnlockCount
      )
      .subscribe(response => {
        if (response.ok === true) {
          $('#magicInsightsBulkUnlock').modal('hide');
          this.messageService.showInfo(
            'Magic Insights have been unlocked for the selected companies.',
            4000
          );
          this.hasFetchedQuotas = false; // so we retrigger quota getting
          this.onUnlock.emit();
        }
      });
  }




  // -------------- State components --------------
  get showUnlockByIdsMessage(): boolean {
    return !!this.companyIds?.length;
  }

  get showUnlockBySearchMessage(): boolean {
    const isResultCountManageable =
      this.numberOfCompaniesMatchingSearchParams < this.maximumCompaniesToBulkUnlockViaQuery ||
      this.numberOfCompaniesMatchingSearchParams === null;

    return !this.companyIds?.length && !!this.searchParams && isResultCountManageable;
  }

  get showSelectionWarning(): boolean {
    const isResultCountOverLimit = this.numberOfCompaniesMatchingSearchParams > this.maximumCompaniesToBulkUnlockViaQuery;
    const isSelectedCountInvalid = this.selectedMagicInsightsUnlockCount === null || this.selectedMagicInsightsUnlockCount > this.maximumCompaniesToBulkUnlockViaQuery;

    return !this.companyIds?.length && !!this.searchParams && isResultCountOverLimit && isSelectedCountInvalid;
  }

  get canConfirmUnlock(): boolean {
    if (this.loadingState.loadingUnlockMagicInsightsModules) {
      return false;
    }

    if (this.companyIds?.length) {
      return true;
    }

    if (this.searchParams) {
      const isResultCountValid =
        this.numberOfCompaniesMatchingSearchParams !== null &&
        this.numberOfCompaniesMatchingSearchParams <= this.maximumCompaniesToBulkUnlockViaQuery;

      const isSelectedCountValid =
        this.selectedMagicInsightsUnlockCount !== null &&
        this.selectedMagicInsightsUnlockCount <= this.maximumCompaniesToBulkUnlockViaQuery;

      return isResultCountValid || isSelectedCountValid;
    }

    return false;
  }

}
